import { ReactNode, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import {
  reorderByIds,
  useSearchDataContextWithCatalogFallback,
} from 'src/contexts/SearchDataContext';
import { Stack } from 'src/core/ui';
import { useUserHasTableTotalsFeature } from 'src/hooks/useUserHasFeature';
import {
  SalesTableFlattened,
  SalesTableFlattenedProps,
} from 'src/tables/SalesTable/SalesTableFlattened';
import {
  SalesTableFlattenedV2,
  SalesTableFlattenedV2Props,
} from 'src/tables/SalesTable/SalesTableFlattenedV2';
import { isInSearchPage } from 'src/utils/deepLinkUtils';
import { createAggregatedSale } from 'src/utils/saleUtils';
import { SaleQuery, SaleQueryV2 } from 'src/WebApiController';

type SalesFlattenedViewProps = {
  before?: ReactNode;
  topListItemBefore?: ReactNode;
  /**
   * If provided, the list will scroll to the month containing the event with the given posEventId
   */
  scrollToEventId?: string;
  isSearchV2?: boolean;
} & Pick<
  SalesTableFlattenedProps,
  | 'alwaysShowCheckbox'
  | 'showSelectAllOnHeader'
  | 'enableColumnFilters'
  | 'ignoreMaxCount'
> & {
    useTableV2?: boolean;
    /**
     * Only used when useTableV2 is true.
     * If true, the pagination will be handled by the server.
     * This means we have to have pagination (disablePagination will be overridden to false)
     * and we'll listen to pagination and sorting changes and pass them back to the server.
     *
     * requires `onPaginationChange` and `onSortingChange` and `serverSidePageIndex` to be provided.
     */
    serverSidePagination?: boolean;
  } & Pick<
    SalesTableFlattenedV2Props,
    'onPaginationChange' | 'onSortingChange' | 'serverSidePageIndex'
  >;

export const SalesFlattenedView = ({
  before,
  topListItemBefore,
  useTableV2 = false,
  serverSidePagination = false,
  isSearchV2,
  ...rest
}: SalesFlattenedViewProps) => {
  const { mainRoute } = useAppContext();

  const hasTableTotalsFeature = useUserHasTableTotalsFeature();
  const isSearchPage = isInSearchPage(mainRoute);

  const { filterQuery } = useFilterQueryContext<SaleQuery>();
  const {
    eventsTransformed,
    totalItemsCount: totalServerSideRowCount,
    maxResultWindow,
    sortedItemIds,
  } = useSearchDataContextWithCatalogFallback();
  const { salesCount } = useMemo(() => {
    let salesCount = 0;

    (eventsTransformed ?? []).forEach((ev) => {
      salesCount += ev.counts.salesCnt ?? 0;
    });

    return { salesCount };
  }, [eventsTransformed]);

  const sales = useMemo(() => {
    if (eventsTransformed?.every((ev) => ev.entities.sales == null)) {
      return null;
    }
    const flatSales = eventsTransformed
      ?.flatMap((ev) => ev.entities.sales)
      ?.filter((s): s is NonNullable<typeof s> => s != null);

    return flatSales
      ? isSearchV2
        ? reorderByIds(flatSales, sortedItemIds)
        : flatSales
      : flatSales;
  }, [eventsTransformed, isSearchV2, sortedItemIds]);

  const failedToRetrieveData = useMemo(() => {
    if (!isSearchV2) {
      return false;
    }

    // Search V2 only - indicate that we failed to retrieve data for all the events
    // when getCatalogCount returns some events but no sales are found
    // This indicates issue that opensearch data is not matching catalog data
    return eventsTransformed?.some((ev) => ev.entities.getDataFail) ?? false;
  }, [eventsTransformed, isSearchV2]);

  const salesSummaryByEvent = useMemo(
    () =>
      eventsTransformed?.every((ev) => ev.entities.salesSummary == null)
        ? undefined
        : eventsTransformed
            ?.map((ev) => ev.entities.salesSummary)
            ?.filter((s) => s != null)
            ?.map((s) => s!),
    [eventsTransformed]
  );

  const salesFiltered = useMemo(
    () => sales?.filter((s) => s != null).map((s) => s!),
    [sales]
  );

  const salesSummary = useMemo(() => {
    if (isSearchV2) {
      // In Search V2, summary is within one of the events
      return eventsTransformed?.find((ev) => ev.entities.salesSummary != null)
        ?.entities.salesSummary;
    }

    if (hasTableTotalsFeature) {
      if (isSearchPage) {
        // TODO: Remove this once TableTotals_All/TableTotals_Sales is GA'ed
        // Need the fallback for the case where the salesSummaryByEvent is null
        // because without TableTotals_* feature flag, the salesSummaryByEvent is null
        // but we still want to show the totals on search page
        return (
          createAggregatedSale(salesSummaryByEvent) ??
          createAggregatedSale(salesFiltered)
        );
      }

      return createAggregatedSale(salesSummaryByEvent);
    }
  }, [
    isSearchV2,
    hasTableTotalsFeature,
    eventsTransformed,
    isSearchPage,
    salesSummaryByEvent,
    salesFiltered,
  ]);

  return (
    <Stack direction="column" height="full" width="full">
      {before}
      {topListItemBefore}
      {useTableV2 ? (
        <SalesTableFlattenedV2
          {...rest}
          useVirtuoso
          isSearchV2={isSearchV2}
          disablePagination={!serverSidePagination}
          manualPagination={serverSidePagination}
          rowCount={
            serverSidePagination
              ? Math.min(
                  totalServerSideRowCount ?? 0,
                  maxResultWindow ?? Infinity
                ) || undefined
              : undefined
          }
          salesCount={
            filterQuery.entityIds?.length
              ? filterQuery.entityIds.length
              : filterQuery.marketplaceEntityIds?.length
                ? filterQuery.marketplaceEntityIds.length
                : salesCount
          }
          sales={salesFiltered}
          salesSummary={salesSummary}
          failedToRetrieveData={failedToRetrieveData}
          pageSize={
            serverSidePagination
              ? ((filterQuery as SaleQueryV2)?.searchOptions?.take ?? undefined)
              : undefined
          }
          totalCount={
            serverSidePagination ? totalServerSideRowCount : undefined
          }
        />
      ) : (
        <SalesTableFlattened
          {...rest}
          useVirtuoso
          disablePagination
          salesCount={
            filterQuery.entityIds?.length
              ? filterQuery.entityIds.length
              : filterQuery.marketplaceEntityIds?.length
                ? filterQuery.marketplaceEntityIds.length
                : salesCount
          }
          sales={salesFiltered}
          salesSummary={salesSummary}
          failedToRetrieveData={false}
        />
      )}
    </Stack>
  );
};
